/**
 * Roadmap Detail Modal Component
 * 
 * Full modal view for roadmap items with:
 * - Title with accent bar
 * - Full description
 * - Upvote functionality (triggers auth for guests)
 * - Comments section
 * - Sidebar with metadata
 * - Fade in/out animation
 */
import { useState, useEffect } from '@wordpress/element';
import { __ } from '@wordpress/i18n';

const RoadmapDetailModal = ({ 
    item, 
    columns = {},
    onClose, 
    onUpvote,
    isLoggedIn,
    currentUser,
    onAuthRequired,
    products = []
}) => {
    const [isClosing, setIsClosing] = useState(false);

    // Build dynamic status label/color from columns prop.
    const getStatusLabel = (slug) => {
        if (columns[slug] && columns[slug].label) return columns[slug].label;
        return slug;
    };

    const getStatusColor = (slug) => {
        const fallback = { bg: '#f3f4f640', text: '#6b7280' };
        if (columns[slug] && columns[slug].color) {
            return { bg: columns[slug].color + '20', text: columns[slug].color };
        }
        return fallback;
    };

    // Format relative date
    const getRelativeDate = (dateString) => {
        if (!dateString) return __( 'Recently', 'changeloger-pro' );
        const date = new Date(dateString);
        const now = new Date();
        const diff = Math.floor((now - date) / (1000 * 60 * 60 * 24));
        
        if (diff === 0) return __( 'Today', 'changeloger-pro' );
        if (diff === 1) return __( 'Yesterday', 'changeloger-pro' );
        if (diff < 7) return `${diff} days ago`;
        if (diff < 30) return `${Math.floor(diff / 7)} weeks ago`;
        return date.toLocaleDateString();
    };

    // Get product name from ID
    const getProductName = () => {
        if (!item.product) return null;
        
        // If product is already an object with name
        if (typeof item.product === 'object' && item.product.name) {
            return item.product.name;
        }
        
        // If product is an ID, look up in products array
        const productId = typeof item.product === 'object' ? item.product.id : item.product;
        const product = products.find(p => p.id === productId || p.id === parseInt(productId));
        return product ? product.title : null;
    };

    // Handle close with animation
    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            onClose();
        }, 200);
    };

    // Handle backdrop click
    const handleBackdropClick = (e) => {
        if (e.target.classList.contains('cha-modal-overlay')) {
            handleClose();
        }
    };

    // Handle upvote
    const handleUpvote = () => {
        if (onUpvote) onUpvote(item.id);
    };

    // Handle escape key
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') handleClose();
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, []);

    // Prevent body scroll when modal is open
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = '';
        };
    }, []);

    if (!item) return null;

    const status = item.status || 'in_review';
    const productName = getProductName();
    const currentStatusColor = getStatusColor(status);

    return (
        <div 
            className={`cha-modal-overlay active ${isClosing ? 'closing' : ''}`} 
            onClick={handleBackdropClick}
        >
            <div className={`cha-roadmap-detail-modal ${isClosing ? 'closing' : ''}`}>
                {/* Close button */}
                <button className="cha-detail-close" onClick={handleClose}>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                <div className="cha-detail-layout">
                    {/* Main Content */}
                    <div className="cha-detail-main">
                        {/* Title with accent bar */}
                        <div className="cha-detail-header">
                            <div 
                                className="cha-detail-accent-bar"
                                style={{ backgroundColor: currentStatusColor.text }}
                            ></div>
                            <h2 className="cha-detail-title">{item.title}</h2>
                        </div>

                        {/* Description */}
                        <div className="cha-detail-description">
                            {item.description || item.content ? (
                                <div dangerouslySetInnerHTML={{ 
                                    __html: item.description || item.content 
                                }} />
                            ) : (
                                <p className="cha-no-description">{__( 'No description provided.', 'changeloger-pro' )}</p>
                            )}
                        </div>

                        {/* Type Badge */}
                        <div className="cha-detail-type-badge">
                            <span className={`cha-type-badge ${item.type}`}>
                                {item.type === 'manual' ? '📋 ' + __( 'Roadmap Item', 'changeloger-pro' ) : '💡 ' + __( 'From Feedback', 'changeloger-pro' )}
                            </span>
                        </div>
                    </div>

                    {/* Sidebar */}
                    <div className="cha-detail-sidebar">
                        {/* Upvoters - Always clickable */}
                        <div className="cha-sidebar-section">
                            <label>{__( 'Upvoters', 'changeloger-pro' )}</label>
                            <div className="cha-upvoters-display">
                                <button 
                                    className={`cha-upvote-btn ${item.has_voted ? 'voted' : ''}`}
                                    onClick={handleUpvote}
                                    title={__( 'Upvote', 'changeloger-pro' )}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                    </svg>
                                </button>
                                <span className="cha-upvote-count">{item.votes || 0}</span>
                            </div>
                        </div>

                        {/* Status */}
                        <div className="cha-sidebar-section">
                            <label>{__( 'Status', 'changeloger-pro' )}</label>
                            <span 
                                className="cha-status-badge-detail"
                                style={{ 
                                    backgroundColor: currentStatusColor.bg, 
                                    color: currentStatusColor.text 
                                }}
                            >
                                {getStatusLabel(status)}
                            </span>
                        </div>

                        {/* Product - Show name not ID */}
                        {productName && (
                            <div className="cha-sidebar-section">
                                <label>{__( 'Product', 'changeloger-pro' )}</label>
                                <span className="cha-product-badge">
                                    {productName}
                                </span>
                            </div>
                        )}

                        {/* Date */}
                        <div className="cha-sidebar-section">
                            <label>{__( 'Created', 'changeloger-pro' )}</label>
                            <span className="cha-date-text">{getRelativeDate(item.date)}</span>
                        </div>

                        {/* Comments count */}
                        <div className="cha-sidebar-section">
                            <label>{__( 'Comments', 'changeloger-pro' )}</label>
                            <span className="cha-comments-count">
                                {item.comment_count || 0} comments
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RoadmapDetailModal;
